import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_AUDIT_FRAMEWORK_MIDDLE_PANEL } from "../../../../apis/settingsApis/audit-apis/auditApis";
import { GET_CHECK_DETAILS_BY_TYPE } from "../../../../apis/settingsApis/framework-apis/frameworkApis";
import { useAuditContext } from "../../../../hooks/useAuditContext";

export const useControlDetails = (audit, controlId, auditIdFromParams) => {
  const [control, setControl] = useState(null);
  const [selectedCheckType, setSelectedCheckType] = useState(null);

  const auditId = audit?.id || auditIdFromParams;

  const { auditEndDate } = useAuditContext({
    auditId,
  });

  // Fetch audit framework data (contains all control info including auditControlId, statuses, checks)
  const { data: auditFrameworkData, loading: frameworkLoading } = useQuery(
    GET_AUDIT_FRAMEWORK_MIDDLE_PANEL,
    {
      variables: {
        audit_id: audit?.id || auditIdFromParams,
      },
      skip: !(audit?.id || auditIdFromParams),
      fetchPolicy: "cache-and-network",
    }
  );

  // Fetch selected check details
  const {
    data: checkData,
    loading: checkLoading,
    refetch: refetchCheckDetails,
  } = useQuery(GET_CHECK_DETAILS_BY_TYPE, {
    variables: {
      check_type: selectedCheckType,
      audit_id: auditId,
      end_time: auditEndDate,
    },
    skip: !selectedCheckType,
    fetchPolicy: "network-only",
  });

  const checkDetails = checkData?.getCheckDetailsByType;
  const activeCheckId = checkDetails?.active_check_id
    ? parseInt(checkDetails.active_check_id)
    : null;

  // Extract control from audit framework data
  useEffect(() => {
    if (!auditFrameworkData?.getAuditFrameworkMiddlePanel || !controlId) return;

    let foundControl = null;

    auditFrameworkData.getAuditFrameworkMiddlePanel.areaDetails?.forEach((area) => {
      if (!area) return;
      area?.criterias?.forEach((criteria) => {
        if (!criteria) return;
        criteria?.controls?.forEach((ctrl) => {
          if (!ctrl || !ctrl.controlsId) return;
          if (ctrl.controlsId === parseInt(controlId)) {
            foundControl = {
              ...ctrl,
              controlsDomain: area.area_name,
            };
          }
        });
      });
    });

    if (foundControl) {
      setControl(foundControl);
      // Auto-select first check
      if (!selectedCheckType && foundControl.checks && foundControl.checks.length > 0) {
        const firstCheck = foundControl.checks[0];
        setSelectedCheckType(firstCheck.checkType || firstCheck.activeCheckName);
      }
    }
  }, [auditFrameworkData, controlId]);

  // Sort checks by name
  const sortedChecks = useMemo(() => {
    if (!control?.checks) return [];
    return [...control.checks].sort((a, b) => {
      const nameA = (a?.activeCheckName || a?.checkName || "").toLowerCase();
      const nameB = (b?.activeCheckName || b?.checkName || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [control?.checks]);

  const checkCategoryLower = checkDetails?.active_check_category?.toLowerCase() || "";
  const checkCategory = useMemo(() => {
    if (!checkCategoryLower) return null;
    if (checkCategoryLower.includes("pass_with_evidence")) return "pass_with_evidence";
    if (checkCategoryLower.includes("azure")) return "azure_system_check";
    if (checkCategoryLower.includes("aws")) return "aws_system_check";
    if (checkCategoryLower.includes("gcp")) return "gcp_system_check";
    if (checkCategoryLower.includes("people")) return "people_check";
    return null;
  }, [checkCategoryLower]);

  const isLoading = frameworkLoading || !control;

  return {
    control,
    sortedChecks,
    selectedCheckType,
    setSelectedCheckType,
    checkDetails,
    activeCheckId,
    checkCategory,
    isLoading,
    checkLoading,
    refetchCheckDetails,
  };
};
